import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Euro, Pencil, Loader2, Check, X, Moon, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

import { tx, lz } from "@/i18n";
const FIELDS = lz([
  { key: "standaard_pakket", label: "Totaalpakket" },
  { key: "extra_bezoek", label: "Extra bezoek" },
  { key: "schoonmaak_linnen", label: "Schoonmaak + Linnenservice" },
  { key: "winkelbezoek", label: "Winkelbezoek" },
  { key: "zwembadonderhoud", label: "Zwembadonderhoud", note: "per half jaar", ownerConditional: true },
  { key: "tuinonderhoud", label: "Tuinonderhoud", note: "per maand", ownerConditional: true },
]);

const ANDERE_TEXT = lz({ v: "vanaf €25 per uur" });
const NIGHT_LABEL = lz({ v: "Meerprijs late in- of uitcheck" });
const NIGHT_DEFAULT = { nacht_van: "35.00", nacht_tot: "35.00", nacht_zichtbaar: false };
const NIGHT_HOURS = lz({ v: "(tussen 22:00u en 07:00u)" });
const sameAmount = (a, b) => parseFloat(String(a).replace(",", ".")) === parseFloat(String(b).replace(",", "."));

const blank = () => Object.fromEntries(FIELDS.map((f) => [f.key, ""]));

const formatEuro = (v) => {
  if (v === "" || v == null) return "—";
  const n = parseFloat(String(v).replace(",", "."));
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(n);
};

export const ServiceRatesCard = ({ houseId, rates, canEdit, onSaved }) => {
  const current = rates || {};
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  // Fields visible in view mode. Owners (canEdit === false) only see the pool/garden
  // rows when the admin actually filled a price; empty conditional rows stay hidden.
  const night = { ...NIGHT_DEFAULT, ...current };
  const showNight = canEdit || (current.nacht_zichtbaar && current.nacht_van != null);
  const visibleFields = FIELDS.filter((f) => {
    if (canEdit) return true;
    if (f.ownerConditional) return !!(current[f.key] || "").trim();
    return true;
  });

  const startEdit = () => {
    setForm({
      ...Object.fromEntries(FIELDS.map((f) => [f.key, current[f.key] || ""])),
      nacht_van: current.nacht_van ?? NIGHT_DEFAULT.nacht_van, nacht_tot: current.nacht_tot ?? NIGHT_DEFAULT.nacht_tot,
      nacht_zichtbaar: !!current.nacht_zichtbaar,
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/houses/${houseId}/service-rates`, form);
      toast.success(tx("Tarieven opgeslagen"));
      setEditing(false);
      onSaved?.(data.service_rates);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-sm" data-testid="service-rates-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Euro className="h-4 w-4 text-secondary" />
          <h2 className="font-display font-semibold text-lg">{tx("Service Tarieven")}</h2>
        </div>
        {canEdit && !editing && (
          <Button variant="ghost" size="sm" onClick={startEdit} className="gap-1.5 text-muted-foreground" data-testid="edit-service-rates-btn">
            <Pencil className="h-4 w-4" /> {tx("Bewerken")}
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label className="text-xs">
                {f.label}{f.note && <span className="text-muted-foreground font-normal"> ({f.note})</span>}
              </Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">€</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder="0,00"
                  className="pl-7"
                  data-testid={`service-rate-input-${f.key}`}
                />
              </div>
            </div>
          ))}
          <NightEdit form={form} setForm={setForm} />
          <div className="flex items-start justify-between gap-3 text-sm pt-1 border-t border-border mt-1" data-testid="service-rate-andere">
            <span className="text-muted-foreground">{tx("Andere werkzaamheden")}</span>
            <span className="font-medium text-right shrink-0">{ANDERE_TEXT.v}</span>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 gap-1.5" data-testid="save-service-rates-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {tx("Opslaan")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="gap-1.5" data-testid="cancel-service-rates-btn">
              <X className="h-4 w-4" /> {tx("Annuleren")}
            </Button>
          </div>
        </div>
      ) : (
        <dl className="space-y-2.5">
          {visibleFields.map((f) => (
            <div key={f.key} className="flex items-start justify-between gap-3 text-sm" data-testid={`service-rate-${f.key}`}>
              <dt className="text-muted-foreground">
                {f.label}{f.note && <span className="text-muted-foreground/70"> ({f.note})</span>}
              </dt>
              <dd className="font-medium text-right shrink-0">{formatEuro(current[f.key])}</dd>
            </div>
          ))}
          {showNight && (
            <div className="flex items-start justify-between gap-3 text-sm" data-testid="service-rate-nacht">
              <dt className="text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  {NIGHT_LABEL.v}
                  {canEdit && (night.nacht_zichtbaar
                    ? <Eye className="h-3.5 w-3.5 text-emerald-600" title={tx("Zichtbaar voor eigenaar")} data-testid="service-rate-nacht-visible" />
                    : <EyeOff className="h-3.5 w-3.5 text-muted-foreground/60" title={tx("Verborgen voor eigenaar")} data-testid="service-rate-nacht-hidden" />)}
                </span>
                <span className="block text-xs text-muted-foreground/70" data-testid="service-rate-nacht-hours">{NIGHT_HOURS.v}</span>
              </dt>
              {sameAmount(night.nacht_van, night.nacht_tot) ? (
                <dd className="font-medium text-right shrink-0" data-testid="service-rate-nacht-amount">{formatEuro(night.nacht_van)}</dd>
              ) : (
                <dd className="font-medium text-right shrink-0 text-xs leading-5" data-testid="service-rate-nacht-amount">
                  <span className="block">22–0u {formatEuro(night.nacht_van)}</span><span className="block">0–7u {formatEuro(night.nacht_tot)}</span>
                </dd>
              )}
            </div>
          )}
          <div className="flex items-start justify-between gap-3 text-sm" data-testid="service-rate-andere">
            <dt className="text-muted-foreground">{tx("Andere werkzaamheden")}</dt>
            <dd className="font-medium text-right shrink-0">{ANDERE_TEXT.v}</dd>
          </div>
        </dl>
      )}
    </div>
  );
};

function NightEdit({ form, setForm }) {
  const inp = (k) => (
    <div className="relative flex-1">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">€</span>
      <Input type="number" min="0" step="0.01" inputMode="decimal" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        placeholder="0,00" className="pl-7" data-testid={`service-rate-input-${k}`} />
    </div>
  );
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid="service-rate-nacht-edit">
      <Label className="text-xs flex items-center gap-1.5"><Moon className="h-3.5 w-3.5 text-secondary" /> {NIGHT_LABEL.v}</Label>
      <p className="text-[11px] text-muted-foreground -mt-1">{NIGHT_HOURS.v}</p>
      <div className="grid grid-cols-2 gap-2">
        <div><span className="text-[11px] text-muted-foreground">22:00 – 0:00</span>{inp("nacht_van")}</div>
        <div><span className="text-[11px] text-muted-foreground">0:00 – 7:00</span>{inp("nacht_tot")}</div>
      </div>
      <p className="text-[11px] text-muted-foreground">{tx("Wordt automatisch als toeslag meegenomen in het kostenoverzicht.")}</p>
      <label className="flex items-center justify-between gap-2 text-xs text-muted-foreground cursor-pointer">
        {tx("Zichtbaar voor eigenaar")}
        <Switch checked={!!form.nacht_zichtbaar} onCheckedChange={(v) => setForm({ ...form, nacht_zichtbaar: v })} data-testid="service-rate-nacht-toggle" />
      </label>
    </div>
  );
}
